import { createStore } from '@xstate/store';
import { useSelector } from '@xstate/store/react';
import { useUnmount } from 'react-use';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Icon, LoadingIcon, Stack } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import {
  useAmendments,
  useAppointmentId,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { getUserName } from '@eluve/utils';

const appointmentLastInteractionDateFragment = graphql(`
  fragment AppointmentLastInteractionDate on Appointments @_unmask {
    __typename
    id
    lastInteractionDate
  }
`);

const lastUpdatedAtDoctorInteractionNotes = graphql(`
  fragment LastUpdatedDoctorInteractionNotes on AppointmentDoctorInteractions {
    __typename
    updatedAt
    noteSignedAt
    user {
      id
      __typename
      firstName
      lastName
      email
    }
  }
`);

export const pendingUnsavedChangesStore = createStore(
  { pending: false },
  {
    set: (_ctx: { pending: boolean }, event: { value: boolean }) => ({
      pending: event.value,
    }),
  },
);

export const AppointmentLastSaved: React.FC<{
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ onClick }) => {
  const { t } = useTranslation();
  const appointmentId = useAppointmentId();
  const pendingUnsavedChanges = useSelector(
    pendingUnsavedChangesStore,
    (s) => s.context.pending,
  );

  useUnmount(() => {
    pendingUnsavedChangesStore.send({ type: 'set', value: false });
  });

  const additionalNotes = useCompleteFragment({
    fragment: lastUpdatedAtDoctorInteractionNotes,
    key: { appointmentId },
    strict: false,
  });

  const appointmentLastInteraction = useCompleteFragment({
    fragment: appointmentLastInteractionDateFragment,
    key: { id: appointmentId },
    strict: false,
  });

  const { amendments: submittedAmendments } = useAmendments(appointmentId, {
    submittedOnly: true,
  });

  const lastSubmittedAmendment =
    submittedAmendments[submittedAmendments.length - 1];

  const getLastSavedMessage = () => {
    if (lastSubmittedAmendment?.submittedAt) {
      return t('appointmentPage.lastSaved.lastAmendedBy', {
        name: getUserName(lastSubmittedAmendment.user),
        time: formatDate.toMMMMdyyyyWithTime(
          new Date(lastSubmittedAmendment.submittedAt),
        ),
      });
    }
    if (additionalNotes?.noteSignedAt) {
      return t('appointmentPage.lastSaved.lastSignedBy', {
        name: getUserName(additionalNotes.user),
        time: formatDate.toMMMMdyyyyWithTime(
          new Date(additionalNotes.noteSignedAt),
        ),
      });
    }
    if (appointmentLastInteraction?.lastInteractionDate) {
      return t('appointmentPage.lastSaved.lastSavedAt', {
        time: formatDate.toMMMMdyyyyWithTime(
          new Date(appointmentLastInteraction.lastInteractionDate),
        ),
      });
    }
    if (pendingUnsavedChanges) {
      return t('appointmentPage.lastSaved.saving');
    }
    return '';
  };

  const message = getLastSavedMessage();
  if (!message) {
    return null;
  }

  return (
    <div className={onClick ? 'cursor-pointer' : ''} onClick={onClick}>
      <Stack gap={2} direction="horizontal">
        {pendingUnsavedChanges ? (
          <LoadingIcon className="text-border-accent-quaternary" />
        ) : (
          <Icon name="History" />
        )}
        <span
          className={`${'text-sm font-medium text-text-tertiary'} hidden sm:inline`}
        >
          {message}
        </span>
      </Stack>
    </div>
  );
};
